import { useCallback, useMemo, useState } from 'react';
import type { ImageScenario } from '../game/imageTypes';
import { partnerForTool, toolById } from '../game/partners';
import { gradeImage } from '../game/imageScoring';
import { falsePositivePenalty } from '../game/scoring';
import { submitResult } from '../game/identity';
import { play } from '../game/sound';
import { ImageViewer } from './ImageViewer';
import { ShareResult } from './ShareResult';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';
import { BackButton } from './BackButton';
import { ConfirmLeave } from './ConfirmLeave';
import { ScorePanel } from './ScorePanel';
import { CaseCard } from './CaseCard';
import { SiteFooter } from './SiteFooter';

type Phase = 'briefing' | 'playing' | 'results';

interface Props {
  scenario: ImageScenario;
  /** 1-based position in the campaign, for orientation. */
  levelNumber: number;
  levelCount: number;
  /** The level after this one, if there is one. */
  nextLevel?: ImageScenario | undefined;
  onNextLevel: () => void;
  /** Called once when the player submits, so progress can be recorded. */
  onScored: (score: number) => void;
  onExit: () => void;
}

export function ImageGame({
  scenario,
  levelNumber,
  levelCount,
  nextLevel,
  onNextLevel,
  onScored,
  onExit,
}: Props) {
  const [phase, setPhase] = useState<Phase>('briefing');
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  /**
   * Leave, checking first only if there is something to lose.
   *
   * Once the results are up there is nothing in progress, so the same
   * control just exits.
   */
  const leave = () => {
    if (phase === 'playing' && selected.size > 0) {
      setConfirmingLeave(true);
      return;
    }
    onExit();
  };

  const toggle = useCallback((arrowId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(arrowId)) {
        next.delete(arrowId);
        play('retract');
      } else {
        next.add(arrowId);
        play('accuse');
      }
      return next;
    });
  }, []);

  const result = useMemo(() => gradeImage(scenario, selected), [scenario, selected]);

  const arrowById = useMemo(
    () => new Map(scenario.arrows.map((a) => [a.id, a])),
    [scenario],
  );

  if (phase === 'briefing') {
    return (
      <div className="panel">
        <RTNav />
        <div className="panel__inner">
          <BrandHeader />
          <p className="case-eyebrow">
            Case file № {String(levelNumber).padStart(2, '0')} of{' '}
            {String(levelCount).padStart(2, '0')}
          </p>
          <h1>{scenario.title}</h1>
          <p className="lede">
            {toolById(scenario.source.toolId).short} · {scenario.subtitle}
          </p>
          <p>{scenario.brief}</p>

          <p className="source-line">
            Based on <b>{toolById(scenario.source.toolId).name}</b> —{' '}
            {scenario.source.screen}
            {scenario.source.kind === 'recreation' ? ' (recreation)' : ''}.{' '}
            {toolById(scenario.source.toolId).name} is a product of{' '}
            {partnerForTool(scenario.source.toolId).name}. This game is not
            affiliated with or endorsed by{' '}
            {partnerForTool(scenario.source.toolId).name}.{' '}
            <a href="#attribution">Attribution</a>
          </p>

          <h2>How to play</h2>
          <ul className="rules">
            <li>
              <b>Tap a red arrow to accuse it.</b> Tap again to take it back. Only
              the arrows are selectable.
            </li>
            <li>
              <b>Zoom in.</b> Pinch, scroll, or use the zoom buttons — the screen
              is too dense to judge at fit-width, and the detail is the point.
            </li>
            <li>
              <b>Restraint counts.</b> One wrong accusation cancels one correct
              find — about {Math.round(falsePositivePenalty(scenario.defects.length))}{' '}
              points here. Flagging everything scores no better than looking
              properly, and usually worse.
            </li>
          </ul>

          <p className="lede">
            {scenario.arrows.length} arrows. Some number of them mark real
            misconfigurations — you are not told how many.
          </p>

          <button
            type="button"
            className="btn btn--wide"
            onClick={() => {
              play('submit');
              setPhase('playing');
            }}
          >
            Open the screenshot
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--wide"
            style={{ marginTop: 10 }}
            onClick={onExit}
          >
            Back
          </button>

          <SiteFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="app app--image">
      <header className="topbar">
        <BackButton onClick={leave} label="Leave this case" />
        <img
          className="topbar__logo"
          src="/brand/releaseteam-logo-white.png"
          alt="ReleaseTEAM"
          width={301}
          height={66}
        />
        <span className="topbar__divider" aria-hidden="true" />
        <span className="topbar__text">
          <span className="topbar__title">{scenario.title}</span>
          <span className="topbar__sub">
            {toolById(scenario.source.toolId).short} · {scenario.subtitle}
          </span>
        </span>
      </header>

      <ImageViewer
        image={scenario.image}
        arrows={scenario.arrows}
        selected={selected}
        onToggle={toggle}
        {...(phase === 'results' ? { reveal: result.verdicts } : {})}
      />

      <div className="hud">
        <span className="hud__count">
          <strong>{selected.size}</strong>
          accused
        </span>
        <span className="hud__spacer" />
        {phase === 'playing' ? (
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setSelected(new Set())}
              disabled={selected.size === 0}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                play('submit');
                // The verdict cue follows the stamp animation rather than the
                // click, so the sound lands with the picture instead of a beat
                // before it.
                window.setTimeout(() => {
                  play('stamp');
                  window.setTimeout(
                    () => play(result.score >= 70 ? 'solved' : 'cold'),
                    260,
                  );
                }, 90);
                setPhase('results');
                onScored(result.score);
                // Fire-and-forget: a failed submission must never block the
                // player from seeing their result. A share link comes back only
                // if the server accepted it.
                void submitResult({
                  scenarioId: scenario.id,
                  scenarioTitle: scenario.title,
                  score: result.score,
                  rating: result.rating,
                  found: result.found.length,
                  total: scenario.defects.length,
                  missed: result.missed.length,
                  falsePositives: result.falsePositives.length,
                }).then((response) => setShareUrl(response?.shareUrl ?? null));
              }}
            >
              Submit
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={onExit}>
            Finish
          </button>
        )}
      </div>

      {confirmingLeave && (
        <ConfirmLeave
          count={selected.size}
          onLeave={onExit}
          onStay={() => setConfirmingLeave(false)}
        />
      )}

      {phase === 'results' && (
        <div className="panel">
          <div className="panel__inner">
            <ScorePanel
              score={result.score}
              rating={result.rating}
              found={result.found.length}
              total={scenario.defects.length}
              missed={result.missed.length}
              falseAlarms={result.falsePositives.length}
            />

            {result.missed.length > 0 && (
              <>
                <h2>Missed — {result.missed.length}</h2>
                {result.missed.map((defect) => (
                  <div key={defect.id} className="finding finding--missed">
                    <div className="finding__head">
                      <span className="finding__where">
                        Arrow {arrowById.get(defect.arrowId)?.label ?? '?'}
                      </span>
                      <span className={`sev sev--${defect.severity}`}>{defect.severity}</span>
                      <span className="finding__title">{defect.title}</span>
                    </div>
                    <p className="finding__body">{defect.explanation}</p>
                    <p className="finding__fix">
                      <b>Fix:</b> {defect.fix}
                    </p>
                  </div>
                ))}
              </>
            )}

            {result.found.length > 0 && (
              <>
                <h2>Found — {result.found.length}</h2>
                {result.found.map((defect) => (
                  <div key={defect.id} className="finding finding--found">
                    <div className="finding__head">
                      <span className="finding__where">
                        Arrow {arrowById.get(defect.arrowId)?.label ?? '?'}
                      </span>
                      <span className={`sev sev--${defect.severity}`}>{defect.severity}</span>
                      <span className="finding__title">{defect.title}</span>
                    </div>
                    <p className="finding__body">{defect.explanation}</p>
                    <p className="finding__fix">
                      <b>Fix:</b> {defect.fix}
                    </p>
                  </div>
                ))}
              </>
            )}

            {result.falsePositives.length > 0 && (
              <>
                <h2>False alarms — {result.falsePositives.length}</h2>
                {result.falsePositives.map((arrow) => (
                  <div key={arrow.id} className="finding finding--fp">
                    <div className="finding__head">
                      <span className="finding__where">Arrow {arrow.label}</span>
                      <span className="finding__title">This one is fine</span>
                    </div>
                    <p className="finding__body">{arrow.decoyNote}</p>
                  </div>
                ))}
              </>
            )}

            {result.correctRejects.length > 0 && (
              <>
                <h2>Correctly left alone — {result.correctRejects.length}</h2>
                {result.correctRejects.map((arrow) => (
                  <div key={arrow.id} className="finding">
                    <div className="finding__head">
                      <span className="finding__where">Arrow {arrow.label}</span>
                      <span className="finding__title">Not a problem</span>
                    </div>
                    <p className="finding__body">{arrow.decoyNote}</p>
                  </div>
                ))}
              </>
            )}

            <ShareResult
              shareUrl={shareUrl}
              score={result.score}
              rating={result.rating}
              levelNumber={levelNumber}
              levelTitle={scenario.title}
              found={result.found.length}
              total={scenario.defects.length}
              falseAlarms={result.falsePositives.length}
            />

            {/* Onward is the primary action: someone who has just read their
                findings wants the next case, not this one again. */}
            {nextLevel ? (
              <div className="next-level">
                <p className="case-eyebrow">Next case</p>
                <CaseCard
                  scenario={nextLevel}
                  number={levelNumber + 1}
                  onOpen={onNextLevel}
                />
              </div>
            ) : (
              <p className="fineprint" style={{ marginTop: 24 }}>
                That was the last level available. More screenshots are on the way.
              </p>
            )}

            <div className="sheet__actions" style={{ marginTop: 20 }}>
              {/* Offered here because a score has just been added to the
                  player's total, which is the one moment the standing is
                  actually interesting. */}
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  window.location.hash = 'leaderboard';
                }}
              >
                Top scores
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setSelected(new Set());
                  setShareUrl(null);
                  setPhase('briefing');
                }}
              >
                Replay this level
              </button>
              <button type="button" className="btn btn--ghost" onClick={onExit}>
                All levels
              </button>
            </div>

            <SiteFooter />
          </div>
        </div>
      )}
    </div>
  );
}
